import React, { useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface Column {
  key: string;
  label: string;
  span: number; // Grid column span
  align?: 'left' | 'center' | 'right';
}

interface FormField {
  id: string;
  [key: string]: any;
}

interface FormTableProps {
  title: string;
  columns: Column[];
  fields: FormField[];
  watched?: Record<string, any>[]; // Watched form values for auto-add functionality
  errors?: Record<string, any>;
  renderCell: (field: FormField, columnKey: string, index: number) => React.ReactNode;
  onAddRow: () => void;
  onRemoveRow: (index: number) => void;
  canRemoveRow?: (index: number) => boolean;
  addButtonText?: string;
  showRowNumbers?: boolean;
  autoAddRow?: boolean; // Enable auto-add row functionality
  primaryField?: string; // The primary field to check for auto-add (e.g., 'product', 'complaint')
}

export default function FormTable({
  title,
  columns,
  fields,
  watched = [],
  errors,
  renderCell,
  onAddRow,
  onRemoveRow,
  canRemoveRow = () => true,
  addButtonText = "Add Row",
  showRowNumbers = true,
  autoAddRow = true,
  primaryField
}: FormTableProps) {

  // Conservative auto-add logic to prevent excessive rows
  const shouldAutoAdd = useMemo(() => {
    if (!autoAddRow || !primaryField || !watched || watched.length === 0 || fields.length >= 3) {
      return false; // Limit to max 3 rows auto-generation
    }
    
    const lastRow = watched[watched.length - 1];
    return (
      watched.length === fields.length &&
      lastRow?.[primaryField]?.trim() &&
      watched.length <= 2 // Only auto-add up to 2 additional rows
    );
  }, [watched, autoAddRow, primaryField, fields.length]);

  // Auto-add new row when current rows are filled
  useEffect(() => {
    if (shouldAutoAdd) {
      onAddRow();
    }
  }, [shouldAutoAdd, onAddRow]);

  // Helper function to get the correct col-span class
  const getColSpanClass = (span: number) => {
    switch (span) {
      case 1: return 'col-span-1';
      case 2: return 'col-span-2';
      case 3: return 'col-span-3';
      case 4: return 'col-span-4';
      case 5: return 'col-span-5';
      case 6: return 'col-span-6';
      default: return `col-span-${span}`;
    }
  };

  return (
    <div className="space-y-1">
      {/* Compact Table Container */}
      <div className="border border-border rounded-lg shadow-sm bg-card">
        {/* Compact Table Header */}
        <div className="border-b border-border bg-gradient-to-r from-muted/50 to-muted/30">
          <div className="flex py-2">
            {showRowNumbers && (
              <div className="w-8 px-1 text-xs font-bold text-center flex items-center justify-center flex-shrink-0 text-muted-foreground">
                #
              </div>
            )}
            <div className="flex-1 grid grid-cols-10 gap-0 items-center">
              {columns.map((column) => (
                <div
                  key={column.key}
                  className={cn(
                    getColSpanClass(column.span),
                    "px-2 text-xs font-bold text-foreground uppercase tracking-wide",
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right',
                    (!column.align || column.align === 'left') && 'text-left',
                    column.key === columns[0].key && 'px-2' // Compact padding
                  )}
                >
                  {column.label}
                </div>
              ))}
            </div>
            {/* Enterprise Action Column */}
            <div className="w-8 px-1 text-xs font-bold text-center flex items-center justify-center flex-shrink-0 text-muted-foreground">
              <X className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Compact Table Rows */}
        <div>
          {fields.map((field, index) => (
            <div
              key={field.id}
              className={cn(
                "flex items-center hover:bg-accent/50 transition-all duration-200 group border-b border-border py-1 last:border-b-0",
                index % 2 === 0 ? "bg-card" : "bg-muted/30"
              )}
            >
              {/* Row Number - Compact styling */}
              {showRowNumbers && (
                <div className="w-8 px-1 text-center flex-shrink-0">
                  <span className="text-xs text-muted-foreground font-semibold bg-muted rounded-full w-5 h-5 flex items-center justify-center">
                    {index + 1}
                  </span>
                </div>
              )}

              {/* Content Area - Compact spacing */}
              <div className="flex-1 grid grid-cols-10 gap-1 items-center">
                {/* Dynamic Cells */}
                {columns.map((column) => (
                  <div
                    key={column.key}
                    className={cn(
                      getColSpanClass(column.span),
                      "px-0.5 py-0.5",
                      column.key === columns[0].key && 'px-1' // Minimal padding for first column
                    )}
                  >
                    {renderCell(field, column.key, index)}
                  </div>
                ))}
              </div>

              {/* Compact Remove Button */}
              <div className="w-8 px-0.5 py-0.5 text-center flex-shrink-0">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive opacity-60 group-hover:opacity-100 transition-all duration-200 rounded-md"
                  onClick={() => onRemoveRow(index)}
                  disabled={!canRemoveRow(index)}
                  title={`Remove ${title.toLowerCase()}`}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Compact Add Row Button */}
        <div className="bg-gradient-to-r from-muted/30 to-muted/20 px-2 py-2 border-t border-border">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onAddRow}
            className="text-primary hover:text-primary hover:bg-primary/10 text-xs font-semibold h-7 px-3 rounded-md transition-all duration-200"
          >
            <Plus className="w-3 h-3 mr-1" />
            {addButtonText}
          </Button>
        </div>
      </div>      
    </div>
  );
}

// Column configurations for different form types - Now using 10-column grid for content
// Layout: # (40px) + Content Columns (10-column grid) + ACTION (40px) = Maximum content space

export const MedicineTableColumns: Column[] = [
  { key: 'medicine', label: 'MEDICINE', span: 4 },        // Medicine names (4/10 = 40%)
  { key: 'dosage', label: 'DOSAGE', span: 1, align: 'left' },    // 1-0-1 (1/10 = 10%)
  { key: 'taken_when', label: 'WHEN', span: 1, align: 'left' },        // Before/After (1/10 = 10%)
  { key: 'duration', label: 'DURATION', span: 1, align: 'left' }, // 5 days (1/10 = 10%)
  { key: 'quantity', label: 'QTY', span: 1, align: 'left' },     // Numbers (1/10 = 10%)
  { key: 'notes', label: 'NOTES', span: 2 }              // Instructions (2/10 = 20%)
  // Total content: 4+1+1+1+1+2 = 10 ✅
];

export const ComplaintsTableColumns: Column[] = [
  { key: 'complaint', label: 'COMPLAINT', span: 3 },      // Complaint descriptions (3/10 = 30%)
  { key: 'frequency', label: 'FREQUENCY', span: 2, align: 'left' }, // Frequency (2/10 = 20%)
  { key: 'severity', label: 'SEVERITY', span: 1, align: 'left' },   // Mild/Moderate/Severe (1/10 = 10%) - REDUCED
  { key: 'duration', label: 'DURATION', span: 2, align: 'left' },   // Duration text (2/10 = 20%)
  { key: 'date', label: 'DATE', span: 2, align: 'left' }      // Date picker (2/10 = 20%) - INCREASED
  // Total content: 3+2+1+2+2 = 10 ✅
];